import { addToast } from "@/utils";

/**
 * The multipart body the booking-document endpoints expect. Shared by the authed and guest flows —
 * the client UI has no description/permission pickers, so attachments default to the assigned driver.
 */
export const buildAttachmentFormData = (file: File): FormData => {
  const body = new FormData();
  body.append("file_upload", file);
  body.append("description", "");
  body.append("permission", "driver");
  return body;
};

/**
 * Upload each picked file to a booking's job via the caller's per-flow uploader — the authed
 * (`/v2/cust/booking/:jobId/document`) and guest (`/v3/guest/job/:jobId/document/:key`) endpoints
 * differ only in their args, so the caller supplies the mutation call. Never throws: the booking already
 * exists (created or updated), so a failed upload is surfaced rather than failing the whole submit.
 * `verb` tailors the default info toast to the flow ("created" on create, "updated" on edit).
 *
 * Returns the number of files that failed to upload. Pass `{ silent: true }` to suppress the built-in
 * info toast when the caller composes its own message from the returned count (e.g. the edit flow, which
 * needs to distinguish an attachments-only edit whose upload failed — where nothing was actually saved).
 */
export const uploadBookingAttachments = async (
  files: File[],
  upload: (body: FormData) => Promise<unknown>,
  verb: "created" | "updated" = "created",
  options?: { silent?: boolean }
): Promise<number> => {
  if (!files.length) return 0;
  const results = await Promise.allSettled(files.map((file) => upload(buildAttachmentFormData(file))));
  const failed = results.filter((result) => result.status === "rejected").length;
  if (failed > 0 && !options?.silent) {
    addToast("info", `Your booking was ${verb}, but the attachment failed to upload.`);
  }
  return failed;
};
